/* global $, toastr */
import { payCharge, saveCard, jqueryPost } from '../shared/card-payment.mjs';

function getCampaignStepThreeConfig() {
  const configElement = document.getElementById('campaign-step-3-config');

  if (!configElement) {
    return {};
  }

  try {
    return JSON.parse(configElement.textContent);
  } catch (error) {
    return {};
  }
}

// Helper function to escape HTML and prevent XSS in error messages
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Track edit mode states globally
let isCreativeEditMode = false;
let isBudgetEditMode = false;

// Notify launch button guard when edit mode changes
function notifyEditModeChange() {
  const event = new CustomEvent('editModeChanged', {
    detail: {
      isCreativeEditMode: isCreativeEditMode,
      isBudgetEditMode: isBudgetEditMode
    }
  });
  document.dispatchEvent(event);
}

// Inline edit for Creative Instructions (per-social tabs: media link, mentions, hashtags)
function initCreativeEdit() {
  const card = document.querySelector('.review-card.review-card--creative[data-edit-url]');
  if (!card) return;

  const editBtn = card.querySelector('.js-creative-edit-btn');
  const cancelBtn = card.querySelector('.js-creative-cancel-btn');
  const saveBtn = card.querySelector('.js-creative-save-btn');
  const panes = Array.from(card.querySelectorAll('.creative-social-pane'));
  const editUrl = card.getAttribute('data-edit-url');
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  const campaignImage = card.dataset.campaignImage || '';
  const campaignImageName = card.dataset.campaignImageName || '';
  const mediaName = card.dataset.mediaName || '';
  const artistName = card.dataset.artistName || '';

  if (!editBtn || !cancelBtn || !saveBtn || !panes.length) return;

  const getPaneElements = (pane) => ({
    slug: pane.getAttribute('data-social-slug'),
    soundInput: pane.querySelector('.js-sound-input'),
    mentionsInput: pane.querySelector('.js-mentions-input'),
    hashtagsInput: pane.querySelector('.js-hashtags-input'),
    soundError: pane.querySelector('.js-sound-error'),
    mentionsError: pane.querySelector('.js-mentions-error'),
    hashtagsError: pane.querySelector('.js-hashtags-error'),
    soundLink: pane.querySelector('.js-sound-link'),
    soundEdit: pane.querySelector('.js-sound-edit'),
    mentionsText: pane.querySelector('.js-mentions-text'),
    hashtagsText: pane.querySelector('.js-hashtags-text')
  });

  const clearInlineErrors = () => {
    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      [el.soundError, el.mentionsError, el.hashtagsError].forEach((errEl) => {
        if (errEl) {
          errEl.textContent = '';
          errEl.classList.add('d-none');
        }
      });
    });
  };

  const getActivePane = () =>
    card.querySelector('.creative-social-pane.active.show') || panes[0];

  // Snapshot initial values per social to detect changes and enable/disable Save
  let initialBySocial = {};
  panes.forEach((pane) => {
    const el = getPaneElements(pane);
    if (!el.slug || !el.soundInput) return;
    initialBySocial[el.slug] = {
      media_link: el.soundInput.value.trim(),
      mentions: (el.mentionsInput?.value || '').trim(),
      hashtags: (el.hashtagsInput?.value || '').trim()
    };
  });

  // Toggle between read-only view and inline input view (all tabs)
  const setMode = (isEditing) => {
    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      el.soundLink?.classList.toggle('d-none', isEditing);
      el.soundEdit?.classList.toggle('d-none', !isEditing);
      el.mentionsText?.classList.toggle('d-none', isEditing);
      el.mentionsInput?.classList.toggle('d-none', !isEditing);
      el.hashtagsText?.classList.toggle('d-none', isEditing);
      el.hashtagsInput?.classList.toggle('d-none', !isEditing);
      [el.soundError, el.mentionsError, el.hashtagsError].forEach((errEl) => {
        if (errEl) { errEl.classList.add('d-none'); errEl.textContent = ''; }
      });
    });

    editBtn.classList.toggle('d-none', isEditing);
    cancelBtn.classList.toggle('d-none', !isEditing);
    saveBtn.classList.toggle('d-none', !isEditing);
    if (!isEditing) {
      saveBtn.disabled = true;
    }

    isCreativeEditMode = isEditing;
    notifyEditModeChange();
  };

  const isValidHttpUrl = (value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  const RULES = getCampaignStepThreeConfig().socialHandleRules || {};

  const setFieldError = (errEl, message) => {
    if (!errEl) return;
    if (message) {
      errEl.textContent = message;
      errEl.classList.remove('d-none');
    } else {
      errEl.textContent = '';
      errEl.classList.add('d-none');
    }
  };

  const validatePane = (el, { show = true } = {}) => {
    if (!el.slug || !el.soundInput) return true;

    const st = window.SocialTokens;
    const mediaVal = el.soundInput.value.trim();
    const mentionsVal = (el.mentionsInput?.value || '').trim();
    const hashtagsVal = (el.hashtagsInput?.value || '').trim();

    const mediaRes = st && st.validateMediaUrl
      ? st.validateMediaUrl(mediaVal, el.slug, RULES)
      : { valid: !mediaVal || isValidHttpUrl(mediaVal), message: 'Please enter a valid URL.' };
    const mentionsRes = st && st.validateMention
      ? st.validateMention(mentionsVal, el.slug, RULES)
      : { valid: true, message: '' };
    const hashtagsRes = st && st.validateHashtags
      ? st.validateHashtags(hashtagsVal, RULES)
      : { valid: true, message: '' };

    if (show) {
      setFieldError(el.soundError, mediaRes.valid ? '' : mediaRes.message);
      setFieldError(el.mentionsError, mentionsRes.valid ? '' : mentionsRes.message);
      setFieldError(el.hashtagsError, hashtagsRes.valid ? '' : hashtagsRes.message);
    }

    return mediaRes.valid && mentionsRes.valid && hashtagsRes.valid;
  };

  // Save is enabled only when something changed AND every tab is valid.
  const setSaveState = () => {
    let changed = false;
    let allValid = true;

    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      if (!el.slug || !el.soundInput) return;
      const current = {
        media_link: el.soundInput.value.trim(),
        mentions: (el.mentionsInput?.value || '').trim(),
        hashtags: (el.hashtagsInput?.value || '').trim()
      };
      const initial = initialBySocial[el.slug] || { media_link: '', mentions: '', hashtags: '' };

      if (
        current.media_link !== initial.media_link ||
        current.mentions !== initial.mentions ||
        current.hashtags !== initial.hashtags
      ) {
        changed = true;
      }

      if (!validatePane(el, { show: false })) {
        allValid = false;
      }
    });

    saveBtn.disabled = !(changed && allValid);
  };

  // Restore all pane inputs to initial values when canceling
  const resetInputs = () => {
    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      if (!el.slug || !el.soundInput) return;
      const initial = initialBySocial[el.slug] || { media_link: '', mentions: '', hashtags: '' };
      el.soundInput.value = initial.media_link;
      if (el.mentionsInput) el.mentionsInput.value = initial.mentions;
      if (el.hashtagsInput) el.hashtagsInput.value = initial.hashtags;
    });
    clearInlineErrors();
  };

  const buildSoundDisplay = (mediaLinkValue, shouldHide) => {
    if (!mediaLinkValue || !isValidHttpUrl(mediaLinkValue)) {
      const placeholder = document.createElement('span');
      placeholder.className = 'review-media__link js-sound-link review-chip--placeholder';
      placeholder.textContent = 'No media link added';
      placeholder.classList.toggle('d-none', shouldHide);
      return placeholder;
    }

    const link = document.createElement('a');
    link.className = 'review-media__link js-sound-link';
    link.href = mediaLinkValue;
    link.target = '_blank';
    link.classList.toggle('d-none', shouldHide);

    if (campaignImage) {
      const image = document.createElement('img');
      image.src = campaignImage;
      image.alt = campaignImageName;
      image.className = 'review-media__thumb';
      link.appendChild(image);
    } else {
      const icon = document.createElement('i');
      icon.className = 'fa-solid fa-photo-film';
      link.appendChild(icon);
    }

    const title = document.createElement('p');
    const name = document.createElement('span');
    name.textContent = mediaName ? `"${mediaName}"` : mediaLinkValue;
    title.appendChild(name);

    if (artistName) {
      const artist = document.createElement('span');
      artist.textContent = ` by ${artistName}`;
      title.appendChild(artist);
    }

    link.appendChild(title);
    return link;
  };

  // Update read-only display after save
  const updateStatic = (slug, values) => {
    const pane = panes.find((item) => item.getAttribute('data-social-slug') === slug);
    if (!pane) return;

    const el = getPaneElements(pane);
    const mediaLinkValue = (values.media_link || '').trim();
    if (el.soundLink) {
      const shouldHide = el.soundLink.classList.contains('d-none');
      el.soundLink.replaceWith(buildSoundDisplay(mediaLinkValue, shouldHide));
    }
    if (el.mentionsText) {
      const mentionsValue = values.mentions || 'No mentions added';
      el.mentionsText.textContent = mentionsValue;
      if (!values.mentions) {
        el.mentionsText.classList.add('review-chip--placeholder');
      } else {
        el.mentionsText.classList.remove('review-chip--placeholder');
      }
    }
    if (el.hashtagsText) {
      const hashtagsValue = values.hashtags || 'No hashtags added';
      el.hashtagsText.textContent = hashtagsValue;
      if (!values.hashtags) {
        el.hashtagsText.classList.add('review-chip--placeholder');
      } else {
        el.hashtagsText.classList.remove('review-chip--placeholder');
      }
    }
  };

  // Enter edit mode
  editBtn.addEventListener('click', () => {
    setMode(true);
    setSaveState();
  });

  // Exit edit mode without saving
  cancelBtn.addEventListener('click', () => {
    resetInputs();
    setMode(false);
  });

  // Validate and send update request
  saveBtn.addEventListener('click', () => {
    const activePane = getActivePane();
    const activeEl = activePane ? getPaneElements(activePane) : null;

    let firstInvalidSlug = null;
    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      if (!el.slug || !el.soundInput) return;
      if (!validatePane(el, { show: true }) && !firstInvalidSlug) {
        firstInvalidSlug = el.slug;
      }
    });

    if (firstInvalidSlug) {
      const invalidPane = panes.find((p) => p.getAttribute('data-social-slug') === firstInvalidSlug);
      const tabBtn = card.querySelector(`#creative-tab-${firstInvalidSlug}`);
      if (tabBtn && invalidPane && !invalidPane.classList.contains('show')) {
        tabBtn.click();
      }
      saveBtn.disabled = true;
      return;
    }

    const creativeBySocial = {};
    panes.forEach((pane) => {
      const el = getPaneElements(pane);
      if (!el.slug || !el.soundInput) return;
      creativeBySocial[el.slug] = {
        media_link: el.soundInput.value.trim(),
        mentions: (el.mentionsInput?.value || '').trim(),
        hashtags: (el.hashtagsInput?.value || '').trim()
      };
    });

    const payload = {
      active_social: activeEl?.slug || null,
      creative_by_social: creativeBySocial
    };

    if (typeof loader !== 'undefined' && loader) {
      loader.classList.remove('d-none');
    }

    $.ajax({
      url: editUrl,
      type: 'PUT',
      data: {
        _token: csrfToken,
        ...payload,
      },
      success: function (response) {
        if (typeof loader !== 'undefined' && loader) {
          loader.classList.add('d-none');
        }

        const responseCreatives = response?.creative_by_social && typeof response.creative_by_social === 'object'
          ? response.creative_by_social
          : null;

        if (responseCreatives) {
          Object.keys(responseCreatives).forEach((slug) => {
            const values = {
              media_link: (responseCreatives[slug]?.media_link || '').trim(),
              mentions: (responseCreatives[slug]?.mentions || '').trim(),
              hashtags: (responseCreatives[slug]?.hashtags || '').trim()
            };
            initialBySocial[slug] = values;
            updateStatic(slug, values);
          });
        } else {
          Object.keys(creativeBySocial).forEach((slug) => {
            initialBySocial[slug] = {
              media_link: creativeBySocial[slug].media_link,
              mentions: creativeBySocial[slug].mentions,
              hashtags: creativeBySocial[slug].hashtags
            };
            updateStatic(slug, initialBySocial[slug]);
          });
        }

        panes.forEach((paneItem) => {
          const paneEl = getPaneElements(paneItem);
          if (!paneEl.slug || !paneEl.soundInput) return;
          const next = initialBySocial[paneEl.slug];
          if (!next) return;
          paneEl.soundInput.value = next.media_link;
          if (paneEl.mentionsInput) paneEl.mentionsInput.value = next.mentions;
          if (paneEl.hashtagsInput) paneEl.hashtagsInput.value = next.hashtags;
        });

        setMode(false);
        toastr.success('Creative updated successfully!');
      },
      error: function (xhr) {
        if (typeof loader !== 'undefined' && loader) {
          loader.classList.add('d-none');
        }
        clearInlineErrors();
        if (xhr.responseJSON && xhr.responseJSON.errors) {
          const errors = xhr.responseJSON.errors;
          // Map server errors to inline elements per social pane
          // Keys like "creative_by_social.tiktok.mentions" or "creative_by_social.tiktok.media_link"
          Object.keys(errors).forEach((key) => {
            const parts = key.split('.');
            // creative_by_social.<slug>.<field>
            if (parts.length === 3 && parts[0] === 'creative_by_social') {
              const slug = parts[1];
              const field = parts[2];
              const pane = panes.find((p) => p.getAttribute('data-social-slug') === slug);
              if (pane) {
                const el = getPaneElements(pane);
                let errEl = null;
                if (field === 'media_link') errEl = el.soundError;
                else if (field === 'mentions') errEl = el.mentionsError;
                else if (field === 'hashtags') errEl = el.hashtagsError;

                if (errEl) {
                  errEl.textContent = errors[key][0];
                  errEl.classList.remove('d-none');
                }

                // Switch to the tab with the error so user sees it
                const tabBtn = card.querySelector(`#creative-tab-${slug}`);
                if (tabBtn && !pane.classList.contains('show')) {
                  tabBtn.click();
                }
              }
            }
          });

          // General errors (e.g. "creative_by_social" top-level) show on active pane media error
          if (errors['creative_by_social']) {
            const activeEl = getActivePane() ? getPaneElements(getActivePane()) : null;
            if (activeEl?.soundError) {
              activeEl.soundError.textContent = errors['creative_by_social'][0];
              activeEl.soundError.classList.remove('d-none');
            }
          }
        } else {
          const message = xhr.responseJSON?.message || 'Something went wrong!';
          toastr.error(escapeHtml(message));
        }
      },
    });
  });

  panes.forEach((pane) => {
    const el = getPaneElements(pane);
    el.soundInput?.addEventListener('input', function () {
      const res = (window.SocialTokens && window.SocialTokens.validateMediaUrl)
        ? window.SocialTokens.validateMediaUrl(this.value, el.slug, RULES)
        : { valid: !this.value.trim() || isValidHttpUrl(this.value.trim()), message: 'Please enter a valid URL.' };
      setFieldError(el.soundError, res.valid ? '' : res.message);
      setSaveState();
    });
    el.mentionsInput?.addEventListener('input', function () {
      if (window.SocialTokens) this.value = window.SocialTokens.normalizeMention(this.value);
      const res = (window.SocialTokens && window.SocialTokens.validateMention)
        ? window.SocialTokens.validateMention(this.value, el.slug, RULES)
        : { valid: true, message: '' };
      setFieldError(el.mentionsError, res.valid ? '' : res.message);
      setSaveState();
    });
    el.hashtagsInput?.addEventListener('input', function () {
      if (window.SocialTokens) {
        const trailing = /\s$/.test(this.value) ? ' ' : '';
        this.value = window.SocialTokens.normalizeHashtag(this.value) + trailing;
      }
      const res = (window.SocialTokens && window.SocialTokens.validateHashtags)
        ? window.SocialTokens.validateHashtags(this.value, RULES)
        : { valid: true, message: '' };
      setFieldError(el.hashtagsError, res.valid ? '' : res.message);
      setSaveState();
    });
  });
}

document.addEventListener('DOMContentLoaded', initCreativeEdit);

// Inline edit for Budget Recap values (max reward, total budget)
function initBudgetEdit() {
  const card = document.querySelector('.review-card[data-edit-url]:not(.review-card--creative)');
  if (!card) return;

  const editBtn = card.querySelector('.js-budget-edit-btn');
  const cancelBtn = card.querySelector('.js-budget-cancel-btn');
  const saveBtn = card.querySelector('.js-budget-save-btn');
  const viewsText = card.querySelector('.js-views-text');
  const maxRewardText = card.querySelector('.js-max-reward-text');
  const totalBudgetText = card.querySelector('.js-total-budget-text');
  const viewsInput = card.querySelector('.js-views-input');
  const maxRewardInput = card.querySelector('.js-max-reward-input');
  const totalBudgetInput = card.querySelector('.js-total-budget-input');
  const budgetError = card.querySelector('.js-budget-error');
  const editUrl = card.getAttribute('data-edit-url');
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  if (!editBtn || !cancelBtn || !saveBtn || !maxRewardInput || !totalBudgetInput) return;

  // Snapshot initial values to detect changes and enable/disable Save
  let initial = {
    maxReward: maxRewardInput.value.trim(),
    totalBudget: totalBudgetInput.value.trim(),
  };

  // Toggle between read-only view and inline input view
  const setMode = (isEditing) => {
    viewsText?.classList.toggle('d-none', isEditing);
    maxRewardText?.classList.toggle('d-none', isEditing);
    totalBudgetText?.classList.toggle('d-none', isEditing);
    if (viewsInput) viewsInput.classList.toggle('d-none', true); // views always hidden (derived)
    maxRewardInput.classList.toggle('d-none', !isEditing);
    totalBudgetInput.classList.toggle('d-none', !isEditing);
    editBtn.classList.toggle('d-none', isEditing);
    cancelBtn.classList.toggle('d-none', !isEditing);
    saveBtn.classList.toggle('d-none', !isEditing);

    // Update global edit mode state and notify
    isBudgetEditMode = isEditing;
    notifyEditModeChange();
  };

  // Save is enabled only if values changed and all fields have values
  const setSaveState = () => {
    const current = {
      maxReward: maxRewardInput.value.trim().replace(/[^0-9.]/g, ''),
      totalBudget: totalBudgetInput.value.trim().replace(/[^0-9.]/g, ''),
    };

    const changed =
      current.maxReward !== initial.maxReward.replace(/[^0-9.]/g, '') ||
      current.totalBudget !== initial.totalBudget.replace(/[^0-9.]/g, '');

    const allFilled = current.maxReward && current.totalBudget;

    // Validation checks
    const maxRewardNum = parseFloat(current.maxReward) || 0;
    const totalBudgetNum = parseFloat(current.totalBudget) || 0;

    let isValid = true;
    let errorMessage = '';

    // Check for empty fields (only if changed)
    if (changed && !allFilled) {
      isValid = false;
      if (!current.maxReward) {
        errorMessage = 'Max reward is required.';
      } else if (!current.totalBudget) {
        errorMessage = 'Total budget is required.';
      }
    }
    // Check for minimum budget and max reward relationship
    else if (allFilled && changed) {
      // Check if total budget is at least $210
      if (totalBudgetNum < 210) {
        isValid = false;
        errorMessage = 'Total budget must be at least $210.';
      }
      // Check if total budget is greater than or equal to max reward
      else if (totalBudgetNum < maxRewardNum) {
        isValid = false;
        errorMessage = 'Total budget cannot be less than max reward.';
      }
    }

    // Show/hide error message
    if (budgetError) {
      if (!isValid && errorMessage) {
        budgetError.textContent = errorMessage;
        budgetError.classList.remove('d-none');
      } else {
        budgetError.textContent = '';
        budgetError.classList.add('d-none');
      }
    }

    saveBtn.disabled = !(changed && allFilled && isValid);
  };

  // Restore inputs to initial values when canceling
  const resetInputs = () => {
    maxRewardInput.value = initial.maxReward;
    totalBudgetInput.value = initial.totalBudget;
  };

  // Format numbers for display in the recap
  const formatNumber = (num) => {
    return Number(num).toLocaleString('en-US');
  };

  // Enter edit mode
  editBtn.addEventListener('click', () => {
    setMode(true);
    setSaveState();
  });

  // Exit edit mode without saving
  cancelBtn.addEventListener('click', () => {
    resetInputs();
    setMode(false);
    budgetError?.classList.add('d-none');
    if (budgetError) budgetError.textContent = '';
  });

  // Validate and send update request
  saveBtn.addEventListener('click', () => {
    const rawMaxReward = maxRewardInput.value.trim().replace(/[^0-9.]/g, '');
    const rawTotalBudget = totalBudgetInput.value.trim().replace(/[^0-9.]/g, '');

    // Check for empty fields
    if (!rawMaxReward || !rawTotalBudget) {
      if (budgetError) {
        if (!rawMaxReward) {
          budgetError.textContent = 'Max reward is required.';
        } else if (!rawTotalBudget) {
          budgetError.textContent = 'Total budget is required.';
        }
        budgetError.classList.remove('d-none');
      }
      saveBtn.disabled = true;
      return;
    }

    // Validate budget values
    const maxRewardNum = parseFloat(rawMaxReward);
    const totalBudgetNum = parseFloat(rawTotalBudget);

    if (totalBudgetNum < 210) {
      if (budgetError) {
        budgetError.textContent = 'Total budget must be at least $210.';
        budgetError.classList.remove('d-none');
      }
      saveBtn.disabled = true;
      return;
    }

    if (totalBudgetNum < maxRewardNum) {
      if (budgetError) {
        budgetError.textContent = 'Total budget cannot be less than max reward.';
        budgetError.classList.remove('d-none');
      }
      saveBtn.disabled = true;
      return;
    }

    // Show loader
    if (typeof loader !== 'undefined' && loader) {
      loader.classList.remove('d-none');
    }

    $.ajax({
      url: editUrl,
      type: 'PUT',
      data: {
        _token: csrfToken,
        maximum_reward_per_video: rawMaxReward,
        total_budget: rawTotalBudget,
      },
      success: function (response) {
        // Hide loader
        if (typeof loader !== 'undefined' && loader) {
          loader.classList.add('d-none');
        }

        // Reload to recompute platform fee and total charge alongside the new budget.
        window.location.reload();
      },
      error: function (xhr) {
        // Hide loader
        if (typeof loader !== 'undefined' && loader) {
          loader.classList.add('d-none');
        }

        if (xhr.responseJSON && xhr.responseJSON.errors) {
          const errors = Object.values(xhr.responseJSON.errors).flat();
          errors.forEach(function (err) {
            toastr.error(escapeHtml(err));
          });
        } else {
          const message = xhr.responseJSON?.message || 'Something went wrong!';
          toastr.error(escapeHtml(message));
        }
      },
    });
  });

  maxRewardInput.addEventListener('input', setSaveState);
  totalBudgetInput.addEventListener('input', setSaveState);
}

document.addEventListener('DOMContentLoaded', initBudgetEdit);

// Swap the processor select icon based on chosen payment method
function initPaymentSelectIcon() {
  const select = document.getElementById('payment_method');
  if (!select) return;
  const iconEl = select.closest('.review-select--with-icon')?.querySelector('.review-select__icon iconify-icon');
  if (!iconEl) return;

  const updateIcon = () => {
    iconEl.setAttribute('icon', select.value === 'wire' ? 'solar:file-linear' : 'solar:card-linear');
  };

  updateIcon();
  select.addEventListener('change', updateIcon);
}

document.addEventListener('DOMContentLoaded', initPaymentSelectIcon);

// Inline add-new-card flow when saved cards already exist
function initInlineAddCard() {
  const addCardForm = document.getElementById('add-card-inline');
  if (!addCardForm) return;

  const trigger = document.querySelector('.js-add-card-trigger');
  const triggerInput = document.querySelector('.review-add-card__input');
  const cancelBtn = addCardForm.querySelector('.js-add-card-cancel');
  const saveBtn = addCardForm.querySelector('.js-add-card-save');
  const errorEl = addCardForm.querySelector('.js-add-card-error');
  const stripeKey = addCardForm.getAttribute('data-stripe-key');
  const addCardUrl = addCardForm.getAttribute('data-add-card-url');
  const setupIntentUrl = addCardForm.getAttribute('data-setup-intent-url');
  const stripeContainer = document.getElementById('radio-type-container');
  const addCardContainer = addCardForm.closest('.review-add-card');

  if (!trigger || !triggerInput || !cancelBtn || !saveBtn || !stripeKey || !addCardUrl || !setupIntentUrl || !stripeContainer || !addCardContainer) return;

  // Stripe Elements are mounted on demand to avoid unnecessary initialization
  const stripe = Stripe(stripeKey);
  const elements = stripe.elements();
  const elementsStyles = {
    hidePostalCode: true,
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        lineHeight: '40px',
        fontWeight: 300,
        fontFamily: 'Helvetica Neue',
        fontSize: '15px',
        '::placeholder': {
          color: 'black',
        },
      },
    },
  };

  const cardNumber = elements.create('cardNumber', { style: elementsStyles });
  const cardExpiry = elements.create('cardExpiry', { style: elementsStyles });
  const cardCvc = elements.create('cardCvc', { style: elementsStyles });

  let mounted = false;
  let previouslySelectedCard = null;

  // Capture the currently selected card BEFORE the "Add new" trigger is clicked
  trigger.addEventListener('mousedown', (e) => {
    // Save which card is currently selected (before the click changes it)
    const currentlySelected = stripeContainer.querySelector('input[name="user_card_id"]:checked');
    if (currentlySelected && currentlySelected !== triggerInput) {
      previouslySelectedCard = currentlySelected;
    }
  });

  // Show inline form and mount Stripe Elements once
  const showForm = () => {
    addCardForm.classList.remove('d-none');
    if (!mounted) {
      cardNumber.mount('#card-number-add');
      cardExpiry.mount('#card-expiry-add');
      cardCvc.mount('#card-cvc-add');
      mounted = true;
    }
  };

  // Hide form and fully unmount Elements to reset state
  const hideForm = (restorePrevious = false) => {
    addCardForm.classList.add('d-none');
    if (errorEl) {
      errorEl.classList.add('d-none');
      errorEl.textContent = '';
    }
    triggerInput.checked = false;

    // Restore previously selected card only when canceling
    if (restorePrevious && previouslySelectedCard) {
      previouslySelectedCard.checked = true;
    }

    // Clear the saved selection
    previouslySelectedCard = null;

    if (mounted) {
      cardNumber.unmount();
      cardExpiry.unmount();
      cardCvc.unmount();
      mounted = false;
    }
  };

  // Selecting "Add new payment method" unselects existing cards
  const handleTrigger = () => {
    stripeContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    triggerInput.checked = true;
    showForm();
  };

  trigger.addEventListener('click', handleTrigger);
  triggerInput.addEventListener('change', handleTrigger);
  cancelBtn.addEventListener('click', () => hideForm(true));

  // Switching back to a saved card hides the inline add form
  stripeContainer.querySelectorAll('input[name="user_card_id"]').forEach((input) => {
    if (input === triggerInput) return;
    input.addEventListener('change', () => {
      hideForm();
    });
  });

  // Save the card through a SetupIntent (3D Secure if the bank asks), then select it.
  saveBtn.addEventListener('click', () => {
    if (errorEl) {
      errorEl.classList.add('d-none');
      errorEl.textContent = '';
    }

    if (typeof loader !== 'undefined' && loader) {
      loader.classList.remove('d-none');
    }

    saveCard({ stripe, card: cardNumber, setupUrl: setupIntentUrl, addUrl: addCardUrl, post: jqueryPost($) }).then((saved) => {
      if (typeof loader !== 'undefined' && loader) {
        loader.classList.add('d-none');
      }

      if (!saved.ok) {
        if (errorEl) {
          errorEl.textContent = saved.message;
          errorEl.classList.remove('d-none');
        }
        return;
      }

      // Uncheck existing cards
      stripeContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
      });

      // Remove "Default" pill from all other cards
      stripeContainer.querySelectorAll('.review-pill').forEach((pill) => {
        pill.remove();
      });

      // Create and insert new row above the "Add new payment method" block (using DOM methods to prevent XSS)
      const row = document.createElement('div');
      row.className = 'review-payment-row';

      const label = document.createElement('label');
      label.className = 'review-radio';

      const meta = document.createElement('div');
      meta.className = 'review-radio__meta';

      const brandSpan = document.createElement('span');
      brandSpan.textContent = saved.cardType || 'Card';

      const endingSpan = document.createElement('span');
      endingSpan.className = 'review-muted';
      endingSpan.textContent = 'Ending in ' + (saved.last4 || '');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'user_card_id';
      input.checked = true;
      input.value = saved.userCardId || '';

      const mark = document.createElement('span');
      mark.className = 'review-radio__mark';

      meta.appendChild(brandSpan);
      meta.appendChild(endingSpan);
      label.appendChild(meta);
      label.appendChild(input);
      label.appendChild(mark);
      row.appendChild(label);

      // Add "Default" pill to the new card
      const defaultPill = document.createElement('span');
      defaultPill.className = 'review-pill';
      defaultPill.textContent = 'Default';
      row.appendChild(defaultPill);

      stripeContainer.insertBefore(row, addCardContainer);
      hideForm();
    });
  });
}

document.addEventListener('DOMContentLoaded', initInlineAddCard);

// Disable Launch when payment method is missing, "Add new" is selected, or edit mode is active
function initLaunchButtonGuard() {
  const form = document.getElementById('charge-client');
  const submitBtn = document.getElementById('submit-step-3');
  if (!form || !submitBtn) return;

  const campaignType = form.getAttribute('data-campaign-type');
  const isOwner = form.getAttribute('data-is-owner') === '1';
  const milestonesCost = parseFloat(form.getAttribute('data-milestones-cost') || 0);
  const requiresPayment = (campaignType === '1' && !isOwner) || (campaignType === '2' && !isOwner && milestonesCost > 0);

  // For non-payment campaigns, only check edit modes
  if (!requiresPayment) {
    const updateStateForFreeFlow = () => {
      submitBtn.disabled = isCreativeEditMode || isBudgetEditMode;
    };

    updateStateForFreeFlow();
    document.addEventListener('editModeChanged', updateStateForFreeFlow);
    return;
  }

  const addCardForm = document.getElementById('add-card-inline');
  const addCardTriggerInput = document.querySelector('.review-add-card__input');
  const stripeContainer = document.getElementById('radio-type-container');
  const paymentMethod = document.getElementById('payment_method');

  const getSelectedCard = () =>
    stripeContainer?.querySelector('input[name="user_card_id"]:checked');

  const isAddCardOpen = () => addCardForm && !addCardForm.classList.contains('d-none');

  const canLaunch = () => {
    // Check if any edit mode is active
    if (isCreativeEditMode || isBudgetEditMode) {
      return false;
    }

    if (paymentMethod && paymentMethod.value === 'wire') {
      return true;
    }

    // If there are no saved-card radios, allow submit (Stripe form path)
    const radios = stripeContainer?.querySelectorAll('input[name="user_card_id"]');
    if (!radios || radios.length === 0) {
      return true;
    }
    const selected = getSelectedCard();
    if (!selected) return false;
    if (addCardTriggerInput && selected === addCardTriggerInput) {
      return false;
    }
    return true;
  };

  const updateState = () => {
    submitBtn.disabled = !canLaunch();
  };

  updateState();

  stripeContainer?.addEventListener('change', updateState);
  paymentMethod?.addEventListener('change', updateState);

  // Listen for edit mode changes
  document.addEventListener('editModeChanged', updateState);

  const observer = new MutationObserver(() => {
    // Track show/hide of inline add-card form
    updateState();
  });

  if (addCardForm) {
    observer.observe(addCardForm, { attributes: true, attributeFilter: ['class'] });
  }
}

document.addEventListener('DOMContentLoaded', initLaunchButtonGuard);

function initCampaignLaunch() {
  const form = document.getElementById('charge-client');
  if (!form) return;

  const config = getCampaignStepThreeConfig();
  const loader = document.getElementById('loaderEl');
  const submitButton = document.getElementById('submit-step-3');
  const paymentMethod = document.getElementById('payment_method');
  const stripeContainer = document.getElementById('stripe-container');
  const wireElement = document.getElementById('wire-element');
  const cardForm = document.getElementById('card_form');
  const wireError = document.querySelector('.image_error');
  const stripeError = document.querySelector('.stripe-validation-error');
  const campaignType = form.getAttribute('data-campaign-type');
  const isOwner = form.getAttribute('data-is-owner') === '1';
  const milestonesCost = parseFloat(form.getAttribute('data-milestones-cost') || '0');
  const requiresPayment = (campaignType === '1' && !isOwner)
    || (campaignType === '2' && !isOwner && milestonesCost > 0);

  let selectedPaymentMethod = paymentMethod ? paymentMethod.value : 'stripe';
  let stripe = null;
  let cardNumber = null;
  let cardExpiry = null;
  let cardCvc = null;

  function showLoader() {
    if (loader) loader.classList.remove('d-none');
  }

  function hideLoader() {
    if (loader) loader.classList.add('d-none');
  }

  function clearStripeFields() {
    if (cardNumber) cardNumber.clear();
    if (cardExpiry) cardExpiry.clear();
    if (cardCvc) cardCvc.clear();
  }

  function updatePaymentMethod() {
    if (!paymentMethod) return;

    selectedPaymentMethod = paymentMethod.value;
    const isWirePayment = selectedPaymentMethod === 'wire';

    if (wireElement) wireElement.classList.toggle('d-none', !isWirePayment);
    if (stripeContainer) stripeContainer.classList.toggle('d-none', isWirePayment);
    if (cardForm) cardForm.classList.toggle('d-none', isWirePayment);
    if (wireError) wireError.classList.toggle('d-none', !isWirePayment);

    if (isWirePayment) {
      clearStripeFields();
    }

    const hiddenPaymentMethod = form.querySelector('input[name="payment_method"]');
    if (hiddenPaymentMethod) {
      hiddenPaymentMethod.value = selectedPaymentMethod;
    }
  }

  function initializeStripeCardForm() {
    if (!requiresPayment || !config.stripePublishableKey || !window.Stripe) {
      return;
    }

    // Needed with a saved card too: confirming the payment (and any 3D Secure) runs here.
    stripe = window.Stripe(config.stripePublishableKey);

    if (config.hasDefaultUserCard || !document.getElementById('card-number')) {
      return;
    }

    const elements = stripe.elements();
    const elementOptions = {
      hidePostalCode: true,
      style: {
        base: {
          iconColor: '#666EE8',
          color: '#31325F',
          lineHeight: '40px',
          fontWeight: 300,
          fontFamily: 'Helvetica Neue',
          fontSize: '15px',
          '::placeholder': {
            color: 'black'
          }
        }
      }
    };

    cardNumber = elements.create('cardNumber', {style: elementOptions});
    cardExpiry = elements.create('cardExpiry', {style: elementOptions});
    cardCvc = elements.create('cardCvc', {style: elementOptions});

    cardNumber.mount('#card-number');
    cardExpiry.mount('#card-expiry');
    cardCvc.mount('#card-cvc');
  }

  function showSubmitError(error) {
    const response = error.responseJSON || {};
    const errors = response.errors || {};

    if (response.reload) {
      window.location.reload();
      return;
    }

    if (errors.end_campaign_at) {
      window.toastr.error(errors.end_campaign_at[0]);
      return;
    }

    const firstError = Object.values(errors)[0];
    if (firstError && wireError) {
      wireError.textContent = Array.isArray(firstError) ? firstError[0] : firstError;
      return;
    }

    if (response.message && stripeError) {
      stripeError.textContent = response.message;
      stripeError.classList.remove('d-none');
      return;
    }

    window.toastr.error(response.message || 'Something went wrong!');
  }

  function showPaymentError(message) {
    if (stripeError) {
      stripeError.textContent = message;
      stripeError.classList.remove('d-none');
      return;
    }

    window.toastr.error(message, {timeOut: 5000});
  }

  function submitCampaign() {
    if (wireError) wireError.textContent = '';
    if (stripeError) {
      stripeError.textContent = '';
      stripeError.classList.add('d-none');
    }

    window.jQuery.ajax({
      url: form.action,
      type: 'POST',
      enctype: 'multipart/form-data',
      processData: false,
      contentType: false,
      data: new FormData(form),
      success: function (response) {
        if (!response.requires_confirmation) {
          hideLoader();
          window.location.href = response.redirect_url;
          return;
        }

        // Card: confirm with Stripe here (3D Secure if the bank asks); the server settles the
        // charge and launches the campaign once the payment is credited.
        payCharge({
          stripe,
          response,
          card: response.payment_method ? null : cardNumber,
          post: jqueryPost(window.jQuery),
        }).then(function (paid) {
          if (paid.ok) {
            window.location.href = response.redirect_url;
            return;
          }

          hideLoader();
          showPaymentError(paid.message);
        });
      },
      error: function (error) {
        hideLoader();
        showSubmitError(error);
      }
    });
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    showLoader();

    const needsNewCard = requiresPayment
      && selectedPaymentMethod === 'stripe'
      && !config.hasDefaultUserCard;

    if (needsNewCard && (!stripe || !cardNumber)) {
      hideLoader();
      showPaymentError('Payment is not available right now.');
      return;
    }

    submitCampaign();
  });

  if (!requiresPayment && submitButton) {
    submitButton.disabled = false;
  }

  if (paymentMethod) {
    paymentMethod.addEventListener('change', updatePaymentMethod);
    updatePaymentMethod();
  }

  const fileInput = document.getElementById('wire-file-input');
  const fileButton = document.querySelector('.file-upload-select .review-file__button');
  const fileName = document.querySelector('.file-upload-select .file-select-name');

  if (fileInput && fileButton) {
    fileButton.addEventListener('click', function () {
      fileInput.click();
    });

    fileInput.addEventListener('change', function () {
      if (fileInput.files.length && fileName) {
        fileName.textContent = fileInput.files[0].name;
      }
    });
  }

  const backButton = document.getElementById('back-step-2');
  const backLink = document.getElementById('back-step-2-link');

  if (backButton && backLink) {
    backButton.addEventListener('click', function () {
      showLoader();
      window.location.href = backLink.href;
    });
  }

  const deleteModalElement = document.getElementById('deleteDraftEl');
  const deleteModalTrigger = document.getElementById('deleteDraftModalTrigger');
  const deleteButton = document.getElementById('delete-draft');
  let deleteModal = null;

  if (deleteModalElement) {
    deleteModal = new window.bootstrap.Modal(deleteModalElement, {keyboard: false});
  }

  if (deleteModalTrigger && deleteModal) {
    deleteModalTrigger.addEventListener('click', function () {
      deleteModal.show();
    });
  }

  if (deleteButton) {
    deleteButton.addEventListener('click', function () {
      showLoader();

      window.jQuery.ajax({
        url: config.deleteDraftUrl,
        type: 'POST',
        data: {
          _token: document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        success: function (response) {
          if (deleteModal) deleteModal.hide();
          hideLoader();
          window.location.href = response.redirect_url;
        },
        error: function () {
          hideLoader();
          window.toastr.error('Draft error!', {timeOut: 5000});
        }
      });
    });
  }

  initializeStripeCardForm();
}

document.addEventListener('DOMContentLoaded', initCampaignLaunch);
